import ReportToolbarButton from '../shared/ReportToolbarButton';
import ReportLead from '../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { MdOutlineRefresh } from 'react-icons/md';
import { AppVarsContext } from '../../context/AppVarsContext';
import { getReportTabsSx } from '../shared/reportSurfaceStyles';
import { createReportSurfaceTheme, getReportSurfaceSx, reportColors, styleReportChart } from '../shared/report-design';

const SECTIONS = [
  ['overview', 'Overview'],
  ['emails', 'Email'],
  ['focusItems', 'Focus Items'],
  ['messages', 'Messages'],
  ['campaigns', 'Campaigns'],
  ['segments', 'Segments'],
];

const safeNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const formatNumber = (value) => new Intl.NumberFormat('en-US').format(safeNumber(value));
const formatPercent = (value) => `${safeNumber(value).toFixed(1)}%`;
const formatDate = (value) => {
  if (!value) return 'No recent change';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};
const getInitialRange = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const toDateString = (date) => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
  return {
    startDate: sessionStorage.getItem('sDate') || toDateString(sevenDaysAgo),
    endDate: sessionStorage.getItem('eDate') || toDateString(today),
  };
};

const metricDefinitions = {
  emails: (item) => [
    ['Sent', item.sent],
    ['Read rate', formatPercent(item.openRate)],
    ['Clicks', item.clicks],
    ['Failed', item.failed],
  ],
  focusItems: (item) => [
    ['Views', item.views],
    ['Clicks', item.clicks],
    ['Submissions', item.submissions],
    ['Type', item.type || '—'],
  ],
  messages: (item) => [
    ['Sent', item.sent],
    ['Pending', item.pending],
    ['Cancelled', item.cancelled],
    ['Channels', item.channels],
  ],
  campaigns: (item) => [
    ['Events', item.events],
    ['Contacts', item.contacts],
    ['Scheduled', item.scheduled],
    ['Failed', item.failed],
  ],
  segments: (item) => [
    ['Contacts', item.contacts],
    ['Added', item.added],
    ['Removed', item.removed],
    ['Net change', safeNumber(item.added) - safeNumber(item.removed)],
  ],
};

const MetricCard = ({ label, value, helper }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={getReportSurfaceSx(theme)}>
      <CardContent sx={{ display: 'grid', gap: 0.45 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.12em' }}>{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{helper}</Typography>
      </CardContent>
    </Card>
  );
};

const EntityGrid = ({ rows, section }) => {
  const theme = useTheme();
  const label = SECTIONS.find(([key]) => key === section)?.[1] || section;
  if (!rows?.length) return <Alert severity="info">No category-scoped {label.toLowerCase()} had activity or configuration for this client.</Alert>;

  return (
    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' } }}>
      {rows.map((item) => (
        <Card key={`${section}-${item.id}`} elevation={0} sx={{ ...getReportSurfaceSx(theme), height: '100%' }}>
          <CardContent sx={{ display: 'grid', gap: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap title={item.name}>{item.name}</Typography>
                <Typography variant="caption" color="text.secondary">Updated {formatDate(item.dateModified || item.dateAdded)}</Typography>
              </Box>
              <Chip size="small" label={item.isPublished ? 'Published' : 'Draft'} color={item.isPublished ? 'success' : 'default'} variant="outlined" />
            </Stack>
            {item.description ? <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{item.description}</Typography> : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
              {metricDefinitions[section](item).map(([metricLabel, metricValue]) => (
                <Box key={metricLabel} sx={{ p: 1, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">{metricLabel}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{typeof metricValue === 'number' ? formatNumber(metricValue) : metricValue}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

const Overview = ({ report }) => {
  const theme = useTheme();
  const sections = [
    ['Email', report.summary?.emails, report.summary?.emailsSent, 'sends', reportColors.revenue],
    ['Focus items', report.summary?.focusItems, report.summary?.focusInteractions, 'interactions', reportColors.categories[2]],
    ['Messages', report.summary?.marketingMessages, report.summary?.messageDeliveries, 'deliveries', reportColors.categories[3]],
    ['Campaigns', report.summary?.campaigns, report.summary?.campaignEvents, 'events', reportColors.conversions],
    ['Segments', report.summary?.segments, report.summary?.segmentAdds, 'new members', reportColors.categories[5]],
  ];
  return (
    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' } }}>
      {sections.map(([label, count, activity, activityLabel, color]) => (
        <Card key={label} elevation={0} sx={{ ...getReportSurfaceSx(theme), borderTop: `4px solid ${color}` }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ fontWeight: 850 }}>{formatNumber(activity)}</Typography>
            <Typography variant="body2" color="text.secondary">{activityLabel} across {formatNumber(count)} configured</Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default function MauticReport({ cid: cidProp, setLoading: setParentLoading }) {
  const brandTheme = useTheme();
  const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { appVars, appLoading } = useContext(AppVarsContext);
  const cid = cidProp || sessionStorage.getItem('rid');
  const [range, setRange] = useState(getInitialRange);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [section, setSection] = useState('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const publicReportSlug = sessionStorage.getItem('reportSlug') || '';

  const loadReport = useCallback(async (isActive) => {
    if (!appVars?.api_url || !cid) {
      setError(!cid ? 'This report does not have a client ID.' : 'The reporting API is not configured.');
      setLoading(false);
      setParentLoading?.(false);
      return;
    }
    setLoading(true);
    setReport(null);
    setParentLoading?.(true);
    setError(null);
    try {
      const response = await fetch(`${appVars.api_url}/mautic/report`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(publicReportSlug ? { 'X-Report-Slug': publicReportSlug } : {}) },
        body: JSON.stringify({ cid, startDate: range.startDate, endDate: range.endDate }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!isActive()) return;
      if (!response.ok || payload.success === false) throw new Error(payload?.error?.message || `Mautic report failed (${response.status})`);
      setReport(payload);
    } catch (requestError) {
      if (isActive()) setError(requestError.message || 'Mautic reporting is unavailable.');
    } finally {
      if (isActive()) {
        setLoading(false);
        setParentLoading?.(false);
      }
    }
  }, [appVars?.api_url, cid, publicReportSlug, range.endDate, range.startDate, setParentLoading]);

  useEffect(() => {
    if (appLoading) return undefined;
    let active = true;
    loadReport(() => active);
    return () => { active = false; };
  }, [appLoading, loadReport, refreshKey]);

  useEffect(() => {
    const handleRangeChange = (event) => setRange({
      startDate: event?.detail?.sDate || sessionStorage.getItem('sDate'),
      endDate: event?.detail?.eDate || sessionStorage.getItem('eDate'),
    });
    window.addEventListener('dc', handleRangeChange);
    return () => window.removeEventListener('dc', handleRangeChange);
  }, []);

  const chartOptions = useMemo(() => styleReportChart(theme, {
    chart: { type: 'line' },
    colors: [reportColors.revenue, reportColors.conversions, reportColors.categories[5]],
    dataLabels: { enabled: false },
    stroke: { curve: 'straight', width: 3 },
    xaxis: { categories: (report?.daily || []).map((row) => row.date), labels: { rotate: isMobile ? -45 : 0, hideOverlappingLabels: true, formatter: (value) => String(value).slice(5).replace('-', '/') } },
    yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (value) => formatNumber(Math.round(value)) } },

    legend: { position: 'bottom', horizontalAlign: 'left' },
    tooltip: { shared: true },
  }), [isMobile, report?.daily, theme]);
  const chartSeries = useMemo(() => [
    { name: 'Email sends', data: (report?.daily || []).map((row) => safeNumber(row.emailSends)) },
    { name: 'Campaign events', data: (report?.daily || []).map((row) => safeNumber(row.campaignEvents)) },
    { name: 'Segment adds', data: (report?.daily || []).map((row) => safeNumber(row.segmentAdds)) },
  ], [report?.daily]);

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{ p: { xs: 1, sm: 2, lg: 3 }, bgcolor: 'background.default', minWidth: 0,
      '@media print': { p: 0 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2, maxWidth: 1600, mx: 'auto', minWidth: 0 }}>
            <Card elevation={0} sx={getReportSurfaceSx(theme)}>
              {isMobile ? (
                <Select inputProps={{ 'aria-label': 'Mautic report sections' }} fullWidth size="small" value={section} onChange={(event) => setSection(event.target.value)} sx={{ m: 1.5, width: 'calc(100% - 24px)' }}>
                  {SECTIONS.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </Select>
              ) : (
                <Tabs value={section} onChange={(_event, value) => setSection(value)} variant="scrollable" scrollButtons="auto" aria-label="Mautic report sections" sx={{ ...getReportTabsSx(theme), p: 1 }}>
                  {SECTIONS.map(([key, label]) => <Tab key={key} value={key} label={label} />)}
                </Tabs>
              )}
            </Card>
        <Card elevation={0} sx={getReportSurfaceSx(theme)}>
          <CardContent sx={{ p: 2, display: 'flex', alignItems: { md: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <ReportLead eyebrow="Mautic" title="Mautic Performance" description="See how email, onsite messages, and automation engage your audience." actions={<ReportToolbarButton startIcon={<MdOutlineRefresh />} onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>Refresh</ReportToolbarButton>} />
          </CardContent>
        </Card>

        {loading && !report ? (
          <Card sx={getReportSurfaceSx(theme)} aria-label="Loading Mautic performance"><CardContent>
            <Typography variant="body2" color="text.secondary" role="status">Loading marketing automation activity…</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
              {Array.from({ length: 6 }, (_, index) => <Skeleton key={index} variant="rounded" height={100} />)}
            </Box>
          </CardContent></Card>
        ) : null}

        {error ? <Alert severity="error" action={<Button color="inherit" onClick={() => setRefreshKey((value) => value + 1)}>Retry</Button>}>{error}</Alert> : null}
        {report && !report.category?.found ? <Alert severity="warning">No Mautic category has the exact alias “{cid}”. Nothing outside that client boundary was included.</Alert> : null}
        {(report?.coverage?.warnings || []).map((warning) => <Alert key={`${warning.section}-${warning.code}`} severity="info"><strong>{warning.section}:</strong> {warning.message}</Alert>)}
        {report?.coverage?.truncated ? <Alert severity="warning">The Mautic server returned more activity than the safe report limit. Totals shown are partial.</Alert> : null}

        {report ? (
          <>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' } }}>
              <MetricCard label="Email sends" value={formatNumber(report.summary?.emailsSent)} helper={`${formatPercent(report.summary?.emailOpenRate)} read rate`} />
              <MetricCard label="Email clicks" value={formatNumber(report.totals?.email?.clicks)} helper={`${formatPercent(report.summary?.emailClickRate)} click rate`} />
              <MetricCard label="Focus activity" value={formatNumber(report.summary?.focusInteractions)} helper={`${formatNumber(report.summary?.focusItems)} focus items`} />
              <MetricCard label="Messages sent" value={formatNumber(report.summary?.messageDeliveries)} helper={`${formatNumber(report.summary?.marketingMessages)} messages`} />
              <MetricCard label="Campaign events" value={formatNumber(report.summary?.campaignEvents)} helper={`${formatNumber(report.summary?.campaigns)} campaigns`} />
              <MetricCard label="Segment adds" value={formatNumber(report.summary?.segmentAdds)} helper={`${formatNumber(report.summary?.segments)} segments`} />
            </Box>

            <Card elevation={0} sx={getReportSurfaceSx(theme)}>
              <CardContent>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Activity trend</Typography>
                <Typography variant="body2" color="text.secondary">Compare daily email sends, campaign events, and new segment members.</Typography>
                <Box sx={{ mt: 1.5, minHeight: 280, minWidth: 0 }}>{report.daily?.length ? <Chart type="line" height={300} options={chartOptions} series={chartSeries} /> : <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>No daily activity was reported for this date range.</Typography>}</Box>
              </CardContent>
            </Card>



            {section === 'overview' ? <Overview report={report} /> : <EntityGrid rows={report[section]} section={section} />}
          </>
        ) : null}
      </Box>
    </Box>
    </ThemeProvider>
  );
}
